'use strict';

var _ = require('lodash');

/**
 * Market Analysis - Market tab - Grid
 */
var MarketAnalysisGridTransformerFactory = function MarketAnalysisGridTransformerFactory($filter, DataTransformer, bookingsIntakeTransformationService, mergeScenarioDataService, momentService, hepService, marketLiveDataService, metricReferenceDataService, dtdFilteringService) {
  var rmMomentFormat = $filter('rmMomentFormat');
  function MarketAnalysisGridTransformer() {
    DataTransformer.call(this, ['market'], ['market-demand-analysis'], 1);
  }
  function setLastRefreshTime(timeStamp) {
    if (timeStamp) {
      marketLiveDataService.setLastRefreshTime('market-demand-analysis', momentService.of(timeStamp, momentService.FIELD.EVENT_TIME_STAMP));
    }
  }
  MarketAnalysisGridTransformer.prototype = Object.create(DataTransformer.prototype);
  MarketAnalysisGridTransformer.constructor = MarketAnalysisGridTransformer;
  MarketAnalysisGridTransformer.prototype.canTransform = function (sourceType, targetType) {
    return this.targetType.indexOf(targetType) > -1 && this.sourceType.indexOf(sourceType) > -1;
  };
  MarketAnalysisGridTransformer.prototype.isCacheable = function () {
    return true;
  };
  MarketAnalysisGridTransformer.prototype.getCacheKey = function (metrics) {
    return this.sourceType.join('-') + '-' + this.targetType.join('-') + '-' + metrics.join('-');
  };
  MarketAnalysisGridTransformer.prototype.transform = function (data, metrics, metricDescriptor) {
    var dtdCriteria = _.get(metricDescriptor, 'params.dtdCriteria');
    setLastRefreshTime(data.domain.timeStamp);
    var memoizedMomentOfDepartureDate = _.memoize(getMomentOfDate);
    var memoizedMomentOfLastProcessedDate = _.memoize(getMomentOfDate);
    var memoizedDOWOfMoment = _.memoize(getDOWOfMoment, function (moment) {
      return moment.valueOf();
    });
    var memoizedYearMonthDayOfMoment = _.memoize(getYearMonthDayOfMoment, function (moment) {
      return moment.valueOf();
    });
    var alertThreshold = hepService.getSimpleHepProperties().ALERT_THRESHOLD;
    var transformData = function transformData(marketDemand) {
      var result = [];
      if (!marketDemand) {
        return;
      }
      for (var i = 0, length = marketDemand.length; i < length; i++) {
        var entry = marketDemand[i];
        var entryKey = entry.carrier + entry.market + entry.posName + entry.departureDate;
        var departureDate = memoizedMomentOfDepartureDate(entry.departureDate, momentService.FIELD.DEPARTURE_DATE);
        var lastProcessedDate = memoizedMomentOfLastProcessedDate(entry.eventTimeStamp, momentService.FIELD.EVENT_TIME_STAMP);
        var curDOW = memoizedDOWOfMoment(departureDate);
        var curYMD = memoizedYearMonthDayOfMoment(departureDate);
        var newEntry = {
          entryKey: entryKey,
          marketDemandId: entry.marketDemandId,
          entity: entry.entity,
          season: entry.season,
          dowGroup: entry.dowGroup,
          referenceCurveMarketDemandId: entry.referenceCurveMarketDemandId,
          scenarioName: entry.scenarioName,
          carrier: entry.carrier,
          market: entry.market,
          posName: entry.posName,
          availabilityCurrent: entry.currentAvailability,
          lowestClassAvailability: entry.lowestClass,
          lowestAvailability: entry.lowestSeats,
          lowestClassFare: entry.lowestFare,
          mappedAvailabilityCurrent: entry.mappedCurrentAvailability,
          mappedLowestClassAvailability: entry.mappedLowestClass,
          mappedLowestAvailability: entry.mappedLowestSeats,
          mappedLowestClassFare: entry.mappedLowestFare,
          liveAvailabilityCurrent: entry.currentLiveAvailability,
          liveLowestClassAvailability: entry.liveLowestClass,
          liveLowestAvailability: entry.liveLowestSeats,
          liveLowestClassFare: entry.liveLowestFare,
          mappedLiveAvailabilityCurrent: entry.mappedCurrentLiveAvailability,
          mappedLiveLowestClassAvailability: entry.mappedLiveLowestClass,
          mappedLiveLowestAvailability: entry.mappedLiveLowestSeats,
          mappedLiveLowestClassFare: entry.mappedLiveLowestFare,
          daysToDeparture: entry.daysToDeparture,
          regularRemainingDemandFC: entry.regularRemainingDemandFC,
          effectiveRegularRemainingDemandFC: entry.effectiveRegularSvcRemainingDemandFC,
          mappedRemainingDemandFC: entry.mappedRemainingDemandFC,
          effectiveMappedRemainingDemandFC: entry.effectiveMappedSvcRemainingDemandFC,
          departureDate: curYMD,
          departureDateMoment: departureDate,
          departureDateMomentDDD: curDOW,
          lastProcessedDateMoment: lastProcessedDate,
          numberOfItineraries: entry.numberOfItineraries,
          appliedRules: entry.appliedRules,
          alerts: entry.alertScore,
          eventTimeStamp: entry.eventTimeStamp,
          numberOfAlertRulesApplied: entry.numberOfAlertRulesApplied,
          alertThresholdExceeded: entry.alertScore > alertThreshold,
          maxBookingsIntake: _.maxBy(entry.bookingsIntake, 'thisYear'),
          minBookingsIntake: _.minBy(entry.bookingsIntake, 'thisYear')
        };
        var metricValues = metricReferenceDataService.getMetricsDataForGrid(entry, ['bookings', 'revenue']);
        var entryWithMetrics = _.extend(newEntry, metricValues);
        bookingsIntakeTransformationService.calculateBookingsIntake(entryWithMetrics, entry);
        result.push(entryWithMetrics);
      }
      return result;
    };
    var rawProductionData = data.domain.flattenedAggregatedMarketDemand;
    var rawScenarioData = data.thirdDomain && data.thirdDomain.flattenedAggregatedMarketDemand || [];
    var hasDtdCriteria = dtdCriteria && dtdCriteria.length;
    var dtdFilteredProductionData = hasDtdCriteria ? dtdFilteringService.filterByDtd(rawProductionData, dtdCriteria) : rawProductionData;
    var dtdFilteredScenarioData = hasDtdCriteria ? dtdFilteringService.filterByDtd(rawScenarioData, dtdCriteria) : rawProductionData;
    var productionData = transformData(dtdFilteredProductionData);
    var scenarioData = transformData(dtdFilteredScenarioData);
    return mergeScenarioDataService.merge(productionData, scenarioData);
  };
  function getMomentOfDate(date, field) {
    return momentService.of(date, field);
  }
  function getDOWOfMoment(moment) {
    return rmMomentFormat(moment, 'DAY_OF_WEEK');
  }
  function getYearMonthDayOfMoment(moment) {
    return rmMomentFormat(moment, 'DATE_ISO');
  }
  return MarketAnalysisGridTransformer;
};
MarketAnalysisGridTransformerFactory.$inject = ['$filter', 'DataTransformerFactory', 'BookingsIntakeTransformationService', 'MergeScenarioDataService', 'MomentService', 'HepService', 'MarketLiveDataService', 'MetricReferenceDataService', 'DtdFilteringService'];
module.exports = MarketAnalysisGridTransformerFactory;